import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { clsx } from 'clsx';
import { useBreakpoints } from 'hooks';
import { useBusinessToolSessionStore, useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getAIAssistantReadinessPercent,
  getBusinessPlan,
  getBusinessPlanType,
  getBusinessToolMode,
} from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useExamplePreview } from 'hooks/useExamplePreview';
import { useIsBusinessPlanOwner } from 'hooks/useIsBusinessPlanOwner';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Responsive } from 'components/_basic/Responsive';
import { TooltipComponent } from 'components/_basic/TooltipComponent';
import { UpgradePlanTooltip } from 'components/_basic/UpgradePlanTooltip';
import ModalContext from 'components/ModalManager/modal-context';
import { OnePageTypeSwitch } from 'components/OnePage';
import { SaveProjectModal } from 'components/SaveProjectModal/SaveProjectModal';
import { ShareDocumentWrapper } from 'components/ShareDocumentWrapper';

import { GenerateWithAiButton } from './components/GenerateWithAIButton';
import { PdfDownloadButton } from './PdfDownloadButton';
import { BusinessToolTitleInput } from '../BusinessToolTitleInput';
import { BusinessToolType } from '../BusinessToolType';
import { useBusinessToolAiContext } from '../components/BusinessToolAiProvider';
import { checkIsOnePageBP, checkIsPreviewMode } from '../utils';

import styles from './BusinessToolHeader.module.scss';

interface IBusinessToolHeaderProps {
  className?: string;
}

export const BusinessToolHeader: React.FC<IBusinessToolHeaderProps> = ({ className }) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const { isAuthenticated } = useAuthenticated();
  const { showModal, hideModal } = useContext(ModalContext);
  const { isLoading, generateWholeDocument } = useBusinessToolAiContext();
  const businessToolMode = useBusinessToolSessionStore(getBusinessToolMode);
  const isBusinessPlanOwner = useIsBusinessPlanOwner();
  const { isExamplePage } = useExamplePreview();
  const isEditAvailable = isBusinessPlanOwner || (!isAuthenticated && !isExamplePage);

  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const isGenerateWholeDocumentDisabled = businessPlan.metadata.isGeneratedWholeDocument && !isStandardOrLightTariff;

  const isShareButtonAvailable = isBusinessPlanOwner || (!isAuthenticated && !isExamplePage);

  const AIAssistantReadinessPercent = useBusinessToolStore(getAIAssistantReadinessPercent);
  const bpType = useBusinessToolStore(getBusinessPlanType);
  const isOnePageBP = checkIsOnePageBP(bpType);

  const aiDisabled = AIAssistantReadinessPercent < 100;

  const isPreviewMode = checkIsPreviewMode(businessToolMode);

  const isPDFDownloadAvailable = isStandardOrLightTariff;

  localStorage.removeItem('address');
  localStorage.removeItem('companyName');
  localStorage.removeItem('projectBriefDescription');
  localStorage.removeItem('companyBriefDescription');

  const handleAIButtonClick = () =>
    isAuthenticated
      ? isGenerateWholeDocumentDisabled
        ? () => null
        : generateWholeDocument()
      : showModal(SaveProjectModal, {
          onClose: hideModal,
          title: t('pleaseLogin'),
          description: t('loginForAIGeneration'),
        });

  return (
    <Box className={clsx(styles.container, className)}>
      <Box className={styles.headerGroup}>
        <Box className={styles.titleRow}>
          <Typography className={styles['title']} variant="h4">
            {t('forBankHeader')}
          </Typography>
          {isEditAvailable && (
            <Box className={styles.typeSelect}>
              <BusinessToolType />
            </Box>
          )}
        </Box>
        <Box className={styles.titleInput}>
          <BusinessToolTitleInput readOnly={isExamplePage} />
        </Box>
      </Box>

      <Box className={styles.buttonGroup}>
        {!isPreviewMode && (
          <TooltipComponent
            title={<UpgradePlanTooltip />}
            isTooltipHidden={!isGenerateWholeDocumentDisabled}
            trigger={isMobile ? 'click' : 'hover'}
            leaveDelay={isMobile ? 6500 : 200}
          >
            <GenerateWithAiButton
              aiDisabled={aiDisabled}
              isLoading={isLoading}
              onClick={handleAIButtonClick}
              isGenerateWholeDocumentDisabled={!!isGenerateWholeDocumentDisabled}
            />
          </TooltipComponent>
        )}

        {!isOnePageBP && (
          <TooltipComponent
            title={<UpgradePlanTooltip />}
            isTooltipHidden={isPDFDownloadAvailable}
            trigger={isMobile ? 'click' : 'hover'}
            leaveDelay={isMobile ? 6500 : 200}
            isTitleIsNode
          >
            <PdfDownloadButton />
          </TooltipComponent>
        )}
        {isShareButtonAvailable && (
          <TooltipComponent
            title={<UpgradePlanTooltip />}
            isTooltipHidden={isPDFDownloadAvailable}
            trigger={isMobile ? 'click' : 'hover'}
            leaveDelay={isMobile ? 6500 : 200}
            isTitleIsNode
          >
            <ShareDocumentWrapper />
          </TooltipComponent>
        )}
        <Responsive mobile>{isOnePageBP && <OnePageTypeSwitch />}</Responsive>
      </Box>
    </Box>
  );
};
